import { useMemo } from "react";
import { isUndefined, isNaN } from "lodash";

import { Executor } from "../../../services";
import {
	useCarParkIdsDecoder,
	useCarParkFilterAccessControl,
} from "../../../hooks";

type Nullable<T> = T | undefined;
type FilterId = number | "all";
type FilterIdArray = FilterId[];

type Filters = {
	companyIds?: FilterIdArray;
	taxiServiceIds?: FilterIdArray;
	executorIds?: FilterIdArray;
	executorGroupIds?: FilterIdArray;
	fleetIds?: FilterIdArray;
};

type ActiveFilters = Partial<Record<keyof Filters, boolean>>;

const DEFAULT_FILTERS: Filters = {
	companyIds: ["all"],
	taxiServiceIds: ["all"],
	executorIds: ["all"],
	executorGroupIds: ["all"],
	fleetIds: ["all"],
};

const DEFAULT_ACTIVE_FILTERS: ActiveFilters = {
	companyIds: false,
	taxiServiceIds: false,
	executorIds: false,
	executorGroupIds: false,
	fleetIds: false,
};

const useFilteredExecutors = (
	executors: Executor.Model[],
	filters: Filters = DEFAULT_FILTERS,
	activeFilters: ActiveFilters = DEFAULT_ACTIVE_FILTERS,
): Executor.Model[] => {
	const { isUserFleetOwner } = useCarParkFilterAccessControl<any>();
	const allCarParkIds = useCarParkIdsDecoder();
	const fleetIdsAll = useMemo(() => allCarParkIds(["all"]), [allCarParkIds]);

	return useMemo(() => {
		const shouldSkipStrictFilter = (arr?: FilterIdArray) =>
			!Array.isArray(arr) ||
			arr.length === 0 ||
			(arr.length === 1 && arr[0] === "all");

		const hasCommonElement = (arr1: FilterIdArray, arr2: FilterIdArray) =>
			arr1.some((id) => arr2.includes(id));

		const filterByIds = (id: Nullable<number>, filterIds: FilterIdArray) =>
			typeof id === "number" &&
			!isNaN(id) &&
			filterIds.some((filterId) => filterId.toString() === id.toString());

		const checkStrictFilter = (
			isActive: boolean,
			id: Nullable<number>,
			filterIds: FilterIdArray,
		): boolean =>
			isActive && !shouldSkipStrictFilter(filterIds)
				? !filterByIds(id, filterIds)
				: false;

		const checkSoftFilter = (
			isActive: boolean,
			id: Nullable<number>,
			filterIds: FilterIdArray,
		): boolean => {
			const isEmptyArr =
				isActive && Array.isArray(filterIds) && filterIds.length === 0;
			if (isEmptyArr) return false;

			return isActive && !shouldSkipStrictFilter(filterIds)
				? !filterByIds(id, filterIds)
				: false;
		};

		const checkSoftArrFilter = (
			isActive: boolean,
			ids: FilterIdArray,
			filterIds: FilterIdArray,
		): boolean => {
			const isEmptyArr =
				isActive && Array.isArray(filterIds) && filterIds.length === 0;
			if (isEmptyArr) return isUserFleetOwner;

			const decoderIds =
				isUserFleetOwner && filterIds?.[0] === "all"
					? fleetIdsAll
					: filterIds;

			return isActive && !shouldSkipStrictFilter(decoderIds)
				? !hasCommonElement(ids, decoderIds)
				: false;
		};

		const mergedActiveFilters = {
			...DEFAULT_ACTIVE_FILTERS,
			...activeFilters,
		};
		const mergedFilters = {
			companyIds: filters?.companyIds ?? [],
			taxiServiceIds: filters?.taxiServiceIds ?? [],
			executorIds: filters?.executorIds ?? [],
			executorGroupIds: filters?.executorGroupIds ?? [],
			fleetIds: filters?.fleetIds ?? [],
		};

		const retval = executors.filter(({ id, taxiService, group, cars }) => {
			const companyId = taxiService?.company?.id;
			const groupId = group?.id;

			const fleetIds = cars?.map((car) => car.park?.id ?? -2) ?? [];

			return !(
				checkStrictFilter(
					!!mergedActiveFilters.companyIds,
					companyId,
					mergedFilters.companyIds,
				) ||
				checkStrictFilter(
					!!mergedActiveFilters.taxiServiceIds,
					taxiService?.id,
					mergedFilters.taxiServiceIds,
				) ||
				checkStrictFilter(
					!!mergedActiveFilters.executorIds,
					id,
					mergedFilters.executorIds,
				) ||
				checkSoftFilter(
					!!mergedActiveFilters.executorGroupIds,
					groupId,
					mergedFilters.executorGroupIds,
				) ||
				checkSoftArrFilter(
					!!mergedActiveFilters.fleetIds,
					fleetIds,
					mergedFilters.fleetIds,
				)
			);
		});

		return retval;
	}, [activeFilters, executors, filters, fleetIdsAll, isUserFleetOwner]);
};

export default useFilteredExecutors;
